/**
 * @file Turns one home summary figure, `booked` or `quotedByStage`, into an
 * `AnalyticsDecimalFigure`. Consumers: the Home tiles and goal bar, through
 * `useHomeGoalProgress`. The quote and buy values are added here, exactly,
 * because the server never adds across stage lists. An absent field is refused
 * `notConfigured` with no key, a string that is not a decimal fails with a
 * message, and a refusal object keeps its code and key, so no unanswered
 * figure ever reads as zero.
 */
import type { Snapshot } from 'valtio';

import type { AnalyticsSummary } from '#types';

import { analyticsDecimalFraction, analyticsDecimalSum } from '#types';
import { analyticsSummaryUnansweredFigure } from './analyticsSummaryUnansweredFigure';
import type { AnalyticsDecimalFigure } from './index.types';

export const analyticsSummaryFigure = (
  figure: Snapshot<AnalyticsSummary>['booked' | 'quotedByStage'],
): AnalyticsDecimalFigure => {
  if (
    figure === undefined ||
    (typeof figure === 'object' && 'refusal' in figure)
  )
    return analyticsSummaryUnansweredFigure(figure);
  const decimals =
    typeof figure === 'string' ? [figure] : [figure.quote, figure.buy];
  const malformed = decimals.find(
    (decimal) => analyticsDecimalFraction(decimal) === undefined,
  );
  return malformed === undefined
    ? { status: 'answered', decimal: analyticsDecimalSum(decimals) }
    : {
        status: 'refused',
        answer: {
          status: 'failed',
          message: `The home summary holds no decimal "${malformed}".`,
        },
      };
};
